import { Component, Input, OnDestroy } from '@angular/core';
import { ContentService } from '../../../services/content/content.service';
import { chain } from 'lodash';
import { UtilService } from '../../../services/util/util.service';
import { Subscription } from 'rxjs';
import { DESKTOP_MEDIUM_WIDTH, MOBILE_LARGE_WIDTH } from '../../../services/util/screen-mode.constant';
import { FilterService } from '../filter.service';

const content = require('./filter-modal.content.json');

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss']
})
export class FilterModalComponent implements OnDestroy {
  @Input()
  public total: number;

  @Input()
  public previewTotal: number;

  @Input()
  public mode: string = 'functie';

  public columnsShown: number;

  public rangeValueForPreview: number = 0;

  private ref: any;
  public filters: any = [];
  public options: any = [];
  public salarisFilter: any = {};
  public subscriptions: Subscription[] = [];

  public constructor(private contentService: ContentService, private utilService: UtilService, private filterService: FilterService) {
    this.contentService.loadContent(content);
    this.subscriptions.push(utilService.getScreenMode$().subscribe(() => {
      if (window.innerWidth <= MOBILE_LARGE_WIDTH) {
        this.columnsShown = 1;
      } else if (window.innerWidth <= DESKTOP_MEDIUM_WIDTH) {
        this.columnsShown = 2;
      } else {
        this.columnsShown = 4;
      }
    }));
    this.subscriptions.push(this.filterService.previewTotal.subscribe(total => this.previewTotal = total));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public calculateItemAmountInColumn(amountOfItems: number): number {
    return Math.ceil(amountOfItems / this.columnsShown);
  }

  public setFilters(rawFilters: any): void {
    Object.keys(rawFilters)
      .filter(key => rawFilters[key].type !== 'autocomplete')
      .forEach(key => {
        this.filters.push(rawFilters[key]);
      });
  }

  public resetFilters(): void {
    this.filters
      .forEach(f => f.optieSets
        .forEach(s => s.opties
          .filter(o => o.checked)
          .forEach(o => o.checked = false)
        )
      );
    this.rangeValueForPreview = 0;
    this.options = [];
    this.filterService.previewHits.next(this.getFilterOptions());
  }

  public doFilter(): void {
    this.mergeOptions();
    this.ref.close(this.options);
  }

  private mergeOptions(): void {
    this.options = chain(this.options)
      .groupBy('key')
      .mapValues(function(v) {
        return chain(v)
          .map('value')
          .flattenDeep()
          .value();
      })
      .value();
    if (this.salarisFilter.min) {
      this.options['salaris'] = [this.salarisFilter.min];
    }
  }

  public handleOptionToggle($event, key: string, value: string): void {
    if ($event.checked) {
      this.options.push({
        key: key,
        value: value
      });
      this.filters
        .filter(f => f.zoekVeld === key)
        .forEach(f => f.optieSets
          .forEach(s => s.opties
            .filter(o => o.value === value)
            .forEach(o => o.checked = true)
          )
        )
    } else {
      const index = this.options.findIndex(option => option.key === key && option.value === value);
      if (index > -1) {
        this.options.splice(index, 1);
      }
    }
    this.filterService.previewHits.next(this.getFilterOptions());
  }

  public getFilterOptions(): Array<{key: string, value: string | number}> {
    return [
      ...this.options,
      this.mode === 'functie' ? { key: 'salaris', value: this.rangeValueForPreview } : null
    ].filter(Boolean);
  }

  public parseOptions(newFilterState: any): void {
    this.filters.forEach(filter => {
      if (filter.zoekVeld === 'salaris') {
        delete filter.ondergrens;
      } else {
        filter.optieSets.forEach(optieSet => optieSet.opties.forEach(optie => (optie.checked = false)));
      }
    });
    this.salarisFilter = {};
    this.options = [];

    Object.keys(newFilterState).forEach(key => {
      const filterValues = newFilterState[key];
      const filterForOption = this.filters.find(filter => filter.zoekVeld === key);

      if (!filterForOption) {
        this.options.push({ key: key, value: filterValues });
      } else if (filterForOption.zoekVeld === 'salaris') {
        this.salarisFilter.min = filterValues[0];
        filterForOption.ondergrens = filterValues[0];
      } else {
        filterForOption.optieSets.forEach(optieSet =>
          optieSet.opties
            .filter(optie => filterValues.includes(optie.value))
            .forEach(option => {
              option.checked = true;
              this.options.push({ key: key, value: option.value });
            })
        );
      }
    });
  }

  public handleRangeSelection(min: number, max: number): void {
    this.setMinMax(min, max);
  }

  private setMinMax(min: number, max: number): void {
    if (min) {
      this.salarisFilter.min = min;
      this.rangeValueForPreview = min;
      this.filterService.previewHits.next([...this.options, { key: 'salaris', value: min }]);
    } else if (max) {
      this.salarisFilter.max = max;
      this.rangeValueForPreview = max;
      this.filterService.previewHits.next([...this.options, { key: 'salaris', value: max }]);
    }
  }

  public close(): void {
    this.ref.close(null);
  }
}
